import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { FaBookOpen, FaPrint, FaArrowLeft, FaFilter, FaCalendarAlt } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

interface LedgerTransaction {
  date: string;
  particulars: string;
  voucherType: string;
  voucherNo: string;
  debit: number;
  credit: number;
  runningBalance: {
    amount: number;
    type: 'Dr' | 'Cr';
  };
}

interface LedgerData {
  partyName: string;
  partyType: 'customer' | 'vendor' | 'loan' | 'expense' | 'fd';
  openingBalance: { amount: number; type: 'Dr' | 'Cr' };
  closingBalance: { amount: number; type: 'Dr' | 'Cr' };
  transactions: LedgerTransaction[];
  totals: { debit: number; credit: number };
}

const PartyLedger: React.FC = () => {
  const [partyType, setPartyType] = useState<'customer' | 'vendor' | 'loan' | 'expense' | 'fd'>('customer');
  const [parties, setParties] = useState<any[]>([]);
  const [selectedParty, setSelectedParty] = useState<any>(null);
  
  // Default date range: current financial year (April 1st of current/prev year to today)
  const getInitialDates = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed: 3 = April
    const fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    
    const startDate = `${fyStartYear}-04-01`;
    const endDate = today.toISOString().split('T')[0];
    return { startDate, endDate };
  };

  const { startDate: initialStart, endDate: initialEnd } = getInitialDates();
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  
  const [ledgerData, setLedgerData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch customers, vendors, loans, expense heads, or fixed deposits on type change
  useEffect(() => {
    setParties([]);
    setSelectedParty(null);
    setLedgerData(null);
    
    let endpoint = '';
    if (partyType === 'customer') endpoint = 'customers';
    else if (partyType === 'vendor') endpoint = 'vendors';
    else if (partyType === 'loan') endpoint = 'loans';
    else if (partyType === 'expense') endpoint = 'expense-heads';
    else if (partyType === 'fd') endpoint = 'fixed-deposits';

    axios.get(`${API_URL}/auth/${endpoint}`)
      .then((res) => {
        let mapped: any[] = [];
        if (Array.isArray(res.data)) {
          if (partyType === 'customer' || partyType === 'vendor') {
            mapped = res.data.map((p: any) => ({
              value: p.id,
              label: `${p.company_name} ${p.gst_number ? `(${p.gst_number})` : ''}`,
              company_name: p.company_name
            }));
          } else if (partyType === 'loan') {
            mapped = res.data.map((p: any) => ({
              value: p.id,
              label: p.lender_name,
              company_name: p.lender_name
            }));
          } else if (partyType === 'expense') {
            mapped = res.data.map((p: any) => ({
              value: p.id,
              label: p.name,
              company_name: p.name
            }));
          } else if (partyType === 'fd') {
            mapped = res.data.map((p: any) => ({
              value: p.id,
              label: p.fd_number,
              company_name: p.fd_number
            }));
          }
        }
        setParties(mapped);
      })
      .catch(() => toast.error(`Failed to fetch ${partyType}s.`));
  }, [partyType]);

  const fetchLedger = () => {
    if (!selectedParty) {
      toast.error('Please select a party first.');
      return;
    }

    setLoading(true);
    axios.get(`${API_URL}/auth/party-ledger`, {
      params: {
        partyType,
        partyId: selectedParty.value,
        startDate,
        endDate
      }
    })
      .then((res) => {
        setLedgerData(res.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || 'Failed to generate ledger.');
        setLedgerData(null);
      })
      .finally(() => setLoading(false));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handlePrint = () => {
    const printArea = document.getElementById('ledger-print-area');
    if (!printArea) return;
    
    const content = printArea.innerHTML;
    const printWindow = window.open('', '', 'width=900,height=750');
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Party Ledger - ${ledgerData?.partyName || 'Ledger'}</title>
          <style>
            body {
              font-family: 'Courier New', Courier, monospace;
              padding: 20px;
              color: #000;
              background-color: #fff;
              font-size: 13px;
              line-height: 1.4;
            }
            .ledger-header {
              text-align: center;
              border-bottom: 2px double #000;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .ledger-header h1 {
              margin: 0 0 5px 0;
              font-size: 18px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .ledger-details {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 15px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              padding: 6px 8px;
              text-align: left;
              border-bottom: 1px solid #000;
            }
            th {
              border-top: 1px solid #000;
              border-bottom: 2px solid #000;
              text-transform: uppercase;
              font-size: 12px;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            .bold {
              font-weight: bold;
            }
            .opening-line, .closing-line {
              border-bottom: 1px solid #000;
              padding: 8px;
              font-weight: bold;
            }
            .totals-row td {
              border-top: 1px solid #000;
              border-bottom: 2px double #000;
              font-weight: bold;
            }
            @media print {
              body { padding: 0; }
              @page { margin: 1.5cm; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.close();
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 bg-gradient-to-b from-blue-50/50 via-white to-gray-50/50 min-h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <div>
          <h2 className="text-3xl font-extrabold text-blue-900 flex items-center gap-3">
            <FaBookOpen className="text-blue-600 animate-pulse" /> Party Ledger Report
          </h2>
          <p className="text-sm text-gray-500 mt-1">Generate and print financial statements in standard bookkeeping format.</p>
        </div>
        <button 
          onClick={() => window.history.back()} 
          className="flex items-center gap-2 text-gray-600 hover:text-blue-900 font-semibold px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-100 transition shadow"
        >
          <FaArrowLeft /> Back
        </button>
      </div>

      {/* Filter Card */}
      <div className="bg-white border border-blue-100 rounded-3xl p-6 shadow-xl mb-8">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <FaFilter className="text-blue-500" /> Filter Criteria
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          
          {/* Party Type Select */}
          <div>
            <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-2">Ledger Type</label>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 bg-gray-100 rounded-xl p-1 border border-gray-200 shadow-inner">
              <button
                type="button"
                onClick={() => setPartyType('customer')}
                className={`py-1.5 text-center text-[10px] font-bold rounded-lg transition-all ${
                  partyType === 'customer' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Customer (Dr)
              </button>
              <button
                type="button"
                onClick={() => setPartyType('vendor')}
                className={`py-1.5 text-center text-[10px] font-bold rounded-lg transition-all ${
                  partyType === 'vendor' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Vendor (Cr)
              </button>
              <button
                type="button"
                onClick={() => setPartyType('loan')}
                className={`py-1.5 text-center text-[10px] font-bold rounded-lg transition-all ${
                  partyType === 'loan' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Loan Head (Cr)
              </button>
              <button
                type="button"
                onClick={() => setPartyType('expense')}
                className={`py-1.5 text-center text-[10px] font-bold rounded-lg transition-all ${
                  partyType === 'expense' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Expense Head (Dr)
              </button>
              <button
                type="button"
                onClick={() => setPartyType('fd')}
                className={`py-1.5 text-center text-[10px] font-bold rounded-lg transition-all ${
                  partyType === 'fd' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Fixed Deposit (Dr)
              </button>
            </div>
          </div>

          {/* Searchable Party Dropdown */}
          <div className="md:col-span-1">
            <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-2">
              Select {partyType === 'customer' ? 'Customer' : partyType === 'vendor' ? 'Vendor' : partyType === 'loan' ? 'Loan / Lender' : partyType === 'expense' ? 'Expense Head' : 'Fixed Deposit (FD)'}
            </label>
            <Select
              options={parties}
              value={selectedParty}
              onChange={(opt) => { setSelectedParty(opt); setLedgerData(null); }}
              placeholder={`Search ${partyType === 'fd' ? 'Fixed Deposit' : partyType}...`}
              className="react-select-container text-sm"
              classNamePrefix="react-select"
            />
          </div>

          {/* Date Picker Range */}
          <div>
            <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <FaCalendarAlt className="text-pink-500" /> From Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setLedgerData(null); }}
              className="w-full border-2 border-gray-200 bg-white px-3 py-1.5 rounded-xl text-sm focus:border-blue-500 outline-none transition"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <FaCalendarAlt className="text-pink-500" /> To Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setLedgerData(null); }}
              className="w-full border-2 border-gray-200 bg-white px-3 py-1.5 rounded-xl text-sm focus:border-blue-500 outline-none transition"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={fetchLedger}
            disabled={loading || !selectedParty}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-700 to-indigo-600 hover:from-blue-600 hover:to-indigo-500 text-white rounded-xl font-bold shadow-lg transition disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate Ledger'}
          </button>
        </div>
      </div>

      {/* Ledger Report Display */}
      {ledgerData && (
        <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-2xl animate__animated animate__fadeIn">
          
          <div className="flex justify-end mb-6">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-gradient-to-r from-gray-700 to-slate-600 hover:from-gray-600 hover:to-slate-500 text-white font-bold px-5 py-2.5 rounded-xl shadow-lg transition"
            >
              <FaPrint /> Print / Save PDF
            </button>
          </div>

          {/* Printable Bookkeeping Ledger Container */}
          <div id="ledger-print-area" className="border border-black p-6 bg-white select-all">
            <div className="ledger-header">
              <h1 className="bold text-center text-xl tracking-wider">{ledgerData.partyName}</h1>
              <div className="text-center font-bold text-md text-gray-700">Ledger Account</div>
              <div className="text-center text-xs mt-1">Period: {formatDate(startDate)} to {formatDate(endDate)}</div>
            </div>

            <div className="ledger-details mt-4 text-sm font-semibold flex justify-between">
              <div>Party Type: <span className="uppercase">{partyType}</span></div>
              <div>Statement Generated: {new Date().toLocaleDateString('en-GB')}</div>
            </div>

            <table className="w-full text-sm mt-4">
              <thead>
                <tr>
                  <th className="w-[10%]">Date</th>
                  <th className="w-[45%]">Particulars</th>
                  <th className="w-[12%]">Vch Type</th>
                  <th className="w-[11%]">Vch No</th>
                  <th className="w-[11%] text-right">Debit (₹)</th>
                  <th className="w-[11%] text-right">Credit (₹)</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening Balance Line */}
                <tr className="bold italic bg-gray-50/50">
                  <td>{formatDate(startDate)}</td>
                  <td>Opening Balance</td>
                  <td></td>
                  <td></td>
                  <td className="text-right">
                    {ledgerData.openingBalance.type === 'Dr' ? ledgerData.openingBalance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                  </td>
                  <td className="text-right">
                    {ledgerData.openingBalance.type === 'Cr' ? ledgerData.openingBalance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                  </td>
                </tr>

                {/* Transactions List */}
                {ledgerData.transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-blue-50/20 transition-colors">
                    <td>{formatDate(tx.date)}</td>
                    <td className="max-w-xs truncate">{tx.particulars}</td>
                    <td>{tx.voucherType}</td>
                    <td>{tx.voucherNo}</td>
                    <td className="text-right">
                      {tx.debit > 0 ? tx.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                    </td>
                    <td className="text-right">
                      {tx.credit > 0 ? tx.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                    </td>
                  </tr>
                ))}

                {/* Blank space or separator */}
                <tr>
                  <td colSpan={6} className="py-2 border-none"></td>
                </tr>

                {/* Totals and Closing Balance Row */}
                <tr className="totals-row font-bold bg-gray-50">
                  <td></td>
                  <td>Total Range Transactions</td>
                  <td></td>
                  <td></td>
                  <td className="text-right border-t border-b border-black">
                    {ledgerData.totals.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="text-right border-t border-b border-black">
                    {ledgerData.totals.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                </tr>

                <tr className="bold italic text-blue-900 bg-blue-50/20">
                  <td>{formatDate(endDate)}</td>
                  <td>Closing Balance</td>
                  <td></td>
                  <td></td>
                  <td className="text-right">
                    {ledgerData.closingBalance.type === 'Dr' ? ledgerData.closingBalance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                  </td>
                  <td className="text-right">
                    {ledgerData.closingBalance.type === 'Cr' ? ledgerData.closingBalance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartyLedger;
